import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../lib/utils";

/**
 * 🔴 A BUTTON'S LABEL CANNOT ESCAPE ITS BORDER.
 *
 * Every size used to set a FIXED height (`h-10`) and centre its content. A
 * label that fits is unaffected; a label that wraps is not — three lines of
 * uppercase display type is ~54px of content vertically centred inside a 40px
 * box, so it paints ~7px past the border at BOTH ends. The button looks intact
 * (the border is exactly where it was) while its text runs into the paragraph
 * above it, which is why this reads as a spacing bug in the surrounding layout
 * rather than a bug in the button. Live case: `ESCALATE INTO BROKER
 * RECRUITMENT` on a listing.
 *
 * The fix is `min-h-*` plus real vertical padding, so the box grows to hold
 * whatever it is given. `text-center` because the multi-line text inside a
 * single anonymous flex item is aligned by `text-align`, which `justify-center`
 * does not set; `break-words` so a single unbreakable token cannot escape
 * sideways for the same reason it used to escape downward.
 *
 * BEHAVIOUR-PRESERVING FOR EVERY LABEL THAT FITS. Each size's padding is set so
 * a one-line label's content box stays under the old fixed height, and `min-h`
 * is that same height — so a single-line button renders at exactly the pixel
 * height it always did, in all ~170 consumers. Only a wrapped label changes,
 * and it changes from broken to correct.
 *
 * Shortening labels is a separate and better fix at each call site; this makes
 * the component honest in the meantime, and keeps it honest afterwards.
 */
const buttonVariants = cva(
  "inline-flex items-center justify-center gap-space-2 font-display font-bold uppercase " +
    "text-center break-words " +
    "transition-colors duration-fast ease-standard " +
    "focus-visible:outline-none focus-visible:shadow-goldRing " +
    "disabled:opacity-40 disabled:pointer-events-none disabled:cursor-not-allowed",
  {
    variants: {
      variant: {
        primary:
          "bg-gold text-onyx hover:bg-gold-highlight",
        secondary:
          "bg-transparent text-onyx border-[1.5px] border-gold hover:bg-gold-highlight/10",
        tertiary:
          "bg-transparent text-gold-shadow hover:underline underline-offset-4 px-space-2",
        text:
          "bg-transparent text-gold-shadow hover:underline underline-offset-4 px-space-2",
        onDark:
          "bg-gold text-onyx hover:bg-gold-highlight",
        destructive:
          "bg-status-error text-white-pure hover:opacity-90",
        icon:
          "bg-transparent text-onyx border border-border-light hover:bg-white-warm",
      },
      size: {
        // min-h + py, never a fixed h. See the note above the base string.
        sm: "min-h-8 px-space-3 py-space-1 text-label tracking-button rounded-sm",
        md: "min-h-10 px-[22px] py-space-2 text-label tracking-button rounded-md",
        lg: "min-h-12 px-space-5 py-space-2 text-small tracking-button rounded-md",
        // Square, icon-only, no label to wrap — a fixed box is correct here.
        icon: "h-9 w-9 rounded-md",
      },
    },
    defaultVariants: {
      variant: "primary",
      size: "md",
    },
  }
);

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof buttonVariants> {
  loading?: boolean;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  ({ className, variant, size, loading, children, disabled, ...props }, ref) => (
    <button
      ref={ref}
      className={cn(buttonVariants({ variant, size }), className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...props}
    >
      {loading ? (
        <span
          className="h-4 w-4 animate-spin rounded-pill border-2 border-current border-t-transparent"
          aria-hidden
        />
      ) : null}
      {children}
    </button>
  )
);
Button.displayName = "Button";

export { buttonVariants };
